import { supabase } from './supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Check if a blog post has already been published today.
 * This avoids calling the edge function (and triggering 429s) when there's nothing to do.
 */
async function blogPublishedToday(): Promise<boolean> {
  try {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();

    const { count } = await supabase
      .from('ai_blog_posts')
      .select('id', { count: 'exact', head: true })
      .gte('published_at', startOfDay)
      .lte('published_at', endOfDay);

    return (count || 0) > 0;
  } catch {
    return false;
  }
}

/**
 * Trigger blog generation via edge function.
 * Only calls the function if no post has been published today (unless force=true).
 */
export async function triggerBlogGeneration(force = false): Promise<{ success: boolean; message: string }> {
  if (!force) {
    const alreadyPublished = await blogPublishedToday();
    if (alreadyPublished) {
      return { success: false, message: 'Blog post already published today' };
    }
  }

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-weekly-article`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (response.status === 429) {
      return { success: false, message: data.message || 'Blog post already published today' };
    }

    if (!response.ok) {
      console.error('Edge function error:', data);
      return { success: false, message: data.error || 'Failed to generate blog post' };
    }

    return { success: true, message: data.message || 'Blog post generated successfully' };
  } catch (error) {
    console.error('Error triggering blog generation:', error);
    return { success: false, message: 'Network error calling blog generator' };
  }
}

let schedulerIntervalId: ReturnType<typeof setInterval> | null = null;
let hasRunInitialCheck = false;

/**
 * Set up the automatic daily blog scheduler.
 * Runs an initial check on app load, then polls every 6 hours.
 * The pre-check + edge function guard ensure only one post is generated per day.
 */
export function setupDailyBlogScheduler() {
  if (hasRunInitialCheck) return;
  hasRunInitialCheck = true;

  // Initial check on app load — generates if nothing published today
  triggerBlogGeneration().then(result => {
    if (result.success) {
      console.log('[BlogScheduler] Auto-generated daily post:', result.message);
    }
  });

  // Poll every 6 hours (pre-check + edge function guard prevent duplicates)
  if (schedulerIntervalId) clearInterval(schedulerIntervalId);
  schedulerIntervalId = setInterval(() => {
    triggerBlogGeneration().then(result => {
      if (result.success) {
        console.log('[BlogScheduler] Auto-generated daily post:', result.message);
      }
    });
  }, 6 * 60 * 60 * 1000);
}

export function stopDailyBlogScheduler() {
  if (schedulerIntervalId) {
    clearInterval(schedulerIntervalId);
    schedulerIntervalId = null;
  }
}
